import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, X, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface EmailVerificationBannerProps {
  userEmail?: string;
  isEmailVerified?: boolean;
  onDismiss?: () => void;
}

export function EmailVerificationBanner({ 
  userEmail, 
  isEmailVerified = false,
  onDismiss 
}: EmailVerificationBannerProps) {
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't show banner if email is verified or dismissed
  if (isEmailVerified || isDismissed) {
    return null;
  }

  const handleResendVerification = async () => {
    if (!userEmail) return;
    
    setIsResending(true);
    setResendMessage(null);
    
    try {
      // Use the correct resend configuration for email confirmation
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email: userEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`
        }
      });

      if (error) {
        console.error('Resend error:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        
        if (error.message?.includes('Email rate limit exceeded') || error.message?.includes('rate limit')) {
          setResendMessage('Please wait a few minutes before requesting another email.');
        } else if (error.message?.includes('User not found') || error.message?.includes('user_not_found')) {
          setResendMessage('User not found. You may need to sign up again.');
        } else if (error.message?.includes('email_already_confirmed') || error.message?.includes('already confirmed')) {
          setResendMessage('Your email is already verified! Please refresh the page.');
        } else if (error.message?.includes('signup_disabled')) {
          setResendMessage('Email verification is currently disabled.');
        } else {
          setResendMessage(`Failed to resend: ${error.message || 'Unknown error'}`);
        }
      } else {
        setResendMessage('Verification email sent! Please check your inbox and spam folder.');
      }
    } catch (error: any) {
      console.error('Resend verification error:', error);
      setResendMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-900 mb-1">
              Verify Your Email Address
            </h3>
            <p className="text-sm text-blue-700 mb-3">
              Please check your email ({userEmail}) and click the verification link to secure your account and unlock all features.
            </p>
            
            {resendMessage && (
              <div className={`text-xs mb-2 ${
                resendMessage.includes('sent') ? 'text-green-600' : 'text-red-600'
              }`}>
                {resendMessage}
              </div>
            )}
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleResendVerification}
                disabled={isResending}
                className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50 flex items-center space-x-1"
              >
                {isResending ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <Mail className="w-3 h-3" />
                )}
                <span>{isResending ? 'Sending...' : 'Resend verification email'}</span>
              </button>
              
              {process.env.NODE_ENV === 'development' && (
                <button
                  onClick={() => {
                    setResendMessage('Email verified for testing! Please refresh the page.');
                    // In a real app, this would trigger a proper verification
                  }}
                  className="text-xs font-medium text-green-600 hover:text-green-700 border border-green-200 px-2 py-1 rounded"
                >
                  Test: Mark as verified
                </button>
              )}
            </div>
          </div>
          
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-blue-400 hover:text-blue-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
} 